import React, { useEffect, useRef, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom"; // 1. Import useNavigate
import CookieBanner from "./components/common/CookieBanner";
import useAppSetup from "./hooks/useAppSetup";
import useCookieConsent from "./hooks/useCookieConsent";
import useAuth from "./hooks/useAuth";
import Toolbar from "./pages/Toolbar";
import Footer from "./components/common/Footer";
import AppRoutes from "./components/common/AppRoutes";
import { AdProvider } from "./contexts/AdContext";
import Downtime from "./pages/Downtime";
import SessionExpiredModal from "./components/common/SessionExpiredModal";
import WelcomeTutorial from "./components/common/WelcomeTutorial";
import { Helmet } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useFirebaseMessaging from "./hooks/useFirebaseMessaging";
import WebSiteSchema from "./components/common/WebSiteSchema";
import LocalBusinessSchema from "./components/common/LocalBusinessSchema";
import MetaPixel from "./components/common/MetaPixel";
import MicrosoftClarity from "./components/common/MicrosoftClarity";
import { trackPageView } from "./utils/analyticsClient";
import { captureAttribution } from "./utils/analytics";
import reportWebVitals from "./reportWebVitals";
import { readConsent } from "./utils/consent";
import { allowedGoogleTagIds } from "./utils/googleTags";
import { isBotUserAgent, isStaticContentPath } from "./utils/downtimeGate";
import "./App.css";
import "./styles/WelcomeTutorial.css";
function App() {
  useEffect(() => {
    // Only load vConsole when explicitly enabled for mobile debugging.
    if (
      process.env.NODE_ENV === 'development' &&
      process.env.REACT_APP_ENABLE_VCONSOLE === 'true'
    ) {
      let vConsoleInstance = null;
      import('vconsole').then(({ default: VConsole }) => {
        vConsoleInstance = new VConsole();
      }).catch(() => {}); // Silently fail if vconsole is not installed
      return () => vConsoleInstance?.destroy();
    }
  }, []);

  // Capture Google Ads click IDs / UTM params on the first load so a later
  // conversion (a quote request) can be attributed to the originating ad click.
  useEffect(() => {
    captureAttribution();
  }, []);

  const {
    jobCategories,
    subscriptionPackages,
    reviews,
    isApiDown,
    isDataLoading,
    isReviewsLoading,
    reviewsError,
    retryReviews,
  } = useAppSetup();

  const { user } = useAuth();
  const location = useLocation();
  const mainContentRef = useRef(null);
  const skipToMain = (event) => {
    event.preventDefault();
    mainContentRef.current?.focus();
    mainContentRef.current?.scrollIntoView?.({ block: "start" });
  };
  const skipLink = (
    <a className="app-skip-link" href={`${location.pathname}${location.search}#main-content`} onClick={skipToMain}>
      Skip to main content
    </a>
  );
  const navigate = useNavigate(); // 2. Get navigate function
  const {
    consent,
    consentDecided,
    settingsOpen,
    acceptAll,
    rejectAll,
    saveChoices,
    closeSettings,
  } = useCookieConsent();
  const consentGiven = consent.analytics;
  useEffect(() => { captureAttribution(); }, [consent.analytics, consent.marketing, location.search]);
  useFirebaseMessaging();

  const [showTutorial, setShowTutorial] = useState(false);

  const isHomeownerOrGuest = !user || user.user_type === "homeowner";
  const isAdminUser = user && ['admin', 'sales', 'merchant', 'partner'].includes(user.user_type);
  const isContractorUser = user && user.user_type === "contractor";
  // Includes the pre-auth admin login page — it isn't under /admin but its
  // visits polluted the "real traffic" analytics and Clarity recordings.
  const isOnAdminPath =
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/penguin-admin-access');
  // Embedded partner widget: the page lives inside a third-party iframe, so
  // it gets the chat layout with none of the site chrome, consent banner,
  // ads or tracking pixels.
  const isWidgetPath = location.pathname.startsWith('/widget/');
  const isChatPath = location.pathname.startsWith('/chat') || isWidgetPath;
  const hideChrome = (isAdminUser && isOnAdminPath) || isWidgetPath;
  // Clarity should not record admin or contractor sessions — they pollute homeowner SEO/UX data.
  const shouldLoadClarity = consentGiven && !isAdminUser && !isContractorUser && !isOnAdminPath && !isWidgetPath;

  useEffect(() => {
    reportWebVitals(shouldLoadClarity);
    return () => { reportWebVitals(false); };
  }, [shouldLoadClarity]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Chat route: freeze the DOCUMENT so only the message list scrolls.
  // The 100dvh .app-wrapper--chat shell alone isn't enough — html/body
  // default to overflow:visible, so iOS Safari can still scroll the page
  // (keyboard focus auto-scroll, AdSense stamping height:auto!important
  // on html/body and injecting body-level nodes). Class-based on purpose:
  // the chat drawer's inline body lock (ConversationChat.jsx) saves and
  // restores element.style, and inline styles here would corrupt that.
  // The styles are scoped to max-width:768px in App.css, so rotation and
  // desktop are handled by CSS (the resize listeners below only feed the
  // --pp-chat-vh height var, they don't gate the class).
  //
  // Preserve pinch zoom in Safari and social in-app browsers. Input font
  // sizes and visualViewport handling below handle keyboard layout instead.
  useEffect(() => {
    if (!isChatPath || isApiDown) return undefined;
    if (typeof navigator !== "undefined" && navigator.userAgent.includes("ReactSnap")) {
      return undefined;
    }
    document.documentElement.classList.add("chat-viewport-lock");
    document.body.classList.add("chat-viewport-lock");

    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
    // iOS ONLY: size the chat shell by what is ACTUALLY visible. iOS with
    // viewport-fit=cover makes 100dvh (and html's 100%) include the area
    // behind Safari's bottom toolbar, so a dvh-sized shell leaves the
    // input bar clipped under the toolbar once the document is frozen
    // (real-iPhone video, 2026-07-17). visualViewport.height is the one
    // measure that tracks the visible region — and it shrinks for the
    // software keyboard too, so the shell compresses above the keyboard
    // for free. CSS consumes this as var(--pp-chat-vh, 100dvh).
    // Android must NOT use this: its 100dvh is already correct (no
    // behind-the-toolbar overhang, and interactive-widget=resizes-content
    // handles the keyboard at layout level), and Chrome's visualViewport
    // height proved unreliable here — a stale keyboard-sized value left
    // the whole chat squashed into the top half of the screen (Android
    // report, 2026-07-17 22:10). Without the var, Android falls back to
    // the 100dvh in CSS — the layout it always had right.
    const vv = window.visualViewport;
    const setChatViewportHeight = () => {
      const height = vv ? vv.height : window.innerHeight;
      document.documentElement.style.setProperty(
        "--pp-chat-vh",
        `${Math.round(height)}px`
      );
    };
    // iOS does not reliably deliver a resize when the keyboard closes —
    // observed on-device (22:51 video): arriving on /chat with the
    // keyboard already up (typed in the homepage hero input) left the
    // shell frozen at keyboard height after the keyboard went away.
    // Focus transitions are the keyboard's real lifecycle, so re-measure
    // on every focusin/focusout and then again after the ~250ms keyboard
    // animation (plus a late pass for slow devices). A missed event can
    // now cost at most ~800ms of wrong height, never a stuck layout.
    const settleTimers = [];
    const remeasureThroughSettle = () => {
      while (settleTimers.length) clearTimeout(settleTimers.pop());
      setChatViewportHeight();
      [120, 400, 800].forEach((ms) =>
        settleTimers.push(setTimeout(setChatViewportHeight, ms))
      );
    };
    if (isIOS) {
      remeasureThroughSettle();
      if (vv) vv.addEventListener("resize", setChatViewportHeight);
      window.addEventListener("resize", setChatViewportHeight);
      window.addEventListener("orientationchange", setChatViewportHeight);
      document.addEventListener("focusin", remeasureThroughSettle);
      document.addEventListener("focusout", remeasureThroughSettle);
    }

    return () => {
      document.documentElement.classList.remove("chat-viewport-lock");
      document.body.classList.remove("chat-viewport-lock");
      if (isIOS) {
        if (vv) vv.removeEventListener("resize", setChatViewportHeight);
        window.removeEventListener("resize", setChatViewportHeight);
        window.removeEventListener("orientationchange", setChatViewportHeight);
        document.removeEventListener("focusin", remeasureThroughSettle);
        document.removeEventListener("focusout", remeasureThroughSettle);
        while (settleTimers.length) clearTimeout(settleTimers.pop());
        document.documentElement.style.removeProperty("--pp-chat-vh");
      }
    };
  }, [isChatPath, isApiDown]);

  // First-party page-view tracking (consent-gated inside the client). Excludes
  // admin/contractor sessions so they don't pollute homeowner funnel data —
  // mirrors the Clarity gate above.
  useEffect(() => {
    if (isAdminUser || isContractorUser || isOnAdminPath) return;
    trackPageView(location.pathname);
  }, [location.pathname, consent.analytics, isAdminUser, isContractorUser, isOnAdminPath]);

  const [isAdBlocked, setIsAdBlocked] = useState(false);

  // Suppress ads during react-snap pre-rendering AND defer until after
  // first client mount to prevent React hydration mismatch (#418).
  const isPrerendering = typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap');
  const [adsMounted, setAdsMounted] = useState(false);
  useEffect(() => { if (!isPrerendering) setAdsMounted(true); }, [isPrerendering]);

  useEffect(() => {
    const checkAdBlock = () => {
      const adElement = document.createElement("div");
      adElement.className = "adsbygoogle";
      document.body.appendChild(adElement);
      window.setTimeout(() => {
        if (window.getComputedStyle(adElement).display === "none") {
          setIsAdBlocked(true);
        }
        document.body.removeChild(adElement);
      }, 100);
    };
    checkAdBlock();
  }, []);

  // Tutorial is click-only (first-visit auto-popup removed 2026-07-09): a popup
  // on first open pushed visitors away (co-owner performance test). Visitors open
  // it on demand via the floating "How It Works" button rendered below.

  // Basic consent mode: do not load Google tags until their purpose is allowed.
  useEffect(() => {
    if (isPrerendering || isWidgetPath) return undefined;
    const ids = allowedGoogleTagIds({ analytics: consent.analytics, marketing: consent.marketing });
    if (!ids.length) return undefined;
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function () { window.dataLayer.push(arguments); };
      window.gtag('consent', 'default', {
        ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied',
        analytics_storage: 'denied', security_storage: 'granted',
      });
      window.gtag('js', new Date());
    }
    const configure = () => {
      // Recheck at the deferred boundary; consent may have changed while idle.
      const current = readConsent();
      if (!current.analytics && !current.marketing) return;
      const allowedIds = allowedGoogleTagIds(current);
      if (!allowedIds.length) return;
      if (!document.querySelector('script[src*="googletagmanager.com/gtag"]')) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${allowedIds[0]}`;
        if (window.cspNonce) script.setAttribute('nonce', window.cspNonce);
        document.head.appendChild(script);
      }
      window.__ppConfiguredTags = window.__ppConfiguredTags || new Set();
      allowedIds.forEach((id) => {
        if (window.__ppConfiguredTags.has(id)) return;
        window.__ppConfiguredTags.add(id);
        window.gtag('config', id);
      });
    };
    const idle = window.requestIdleCallback?.(configure, { timeout: 4000 });
    const timer = window.setTimeout(configure, 3000);
    return () => {
      if (idle !== undefined) window.cancelIdleCallback?.(idle);
      window.clearTimeout(timer);
    };
  }, [isPrerendering, isWidgetPath, consent.analytics, consent.marketing]);

  // Consent-state synchronization with gtag, per category: analytics consent
  // drives analytics_storage (+functionality/personalization), marketing
  // consent drives the three ad signals (Consent Mode v2).
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    const analyticsState = consent.analytics ? 'granted' : 'denied';
    const marketingState = consent.marketing ? 'granted' : 'denied';
    window.gtag('consent', 'update', {
      ad_storage: marketingState,
      ad_user_data: marketingState,
      ad_personalization: marketingState,
      analytics_storage: analyticsState,
      functionality_storage: analyticsState,
      personalization_storage: analyticsState,
    });
  }, [consent.analytics, consent.marketing]);

  // Tag Clarity sessions with user metadata. Safety net for admin/contractor
  // sessions that slip past the mount gate (e.g. user role changes mid-session).
  useEffect(() => {
    if (!shouldLoadClarity || typeof window === 'undefined' || typeof window.clarity !== 'function') return;
    const userType = user?.user_type || 'guest';
    try {
      window.clarity('set', 'user_type', userType);
      window.clarity('set', 'is_authenticated', user ? 'yes' : 'no');
    } catch {
      // Clarity may throw if not yet initialized; safe to ignore.
    }
  }, [user, shouldLoadClarity]);

  const trackEvent = (category, action, label) => {
    if (consentGiven && window.gtag) {
      window.gtag("event", action, {
        event_category: category,
        event_label: label,
      });
    }
  };

  const isBot =typeof navigator === 'undefined' || isBotUserAgent(navigator.userAgent);

  if (isApiDown && !isBot && !isStaticContentPath(window.location.pathname)) {
    return <>{skipLink}<main id="main-content" ref={mainContentRef} tabIndex={-1}><Downtime /></main></>;
  }
  
  // Advertising requires marketing permission, including non-personalized ads.
  // Ad-free surfaces (2026-07 decision): homepage, chat and admin stay ad-free —
  // ads there push visitors away for pennies. Content pages (blog, cost guides,
  // location pages) keep their ad units.
  const isAdFreePath = location.pathname === "/" || isChatPath || isOnAdminPath;
  const shouldShowAds =
      consent.marketing &&
      process.env.NODE_ENV !== 'development' &&
      adsMounted &&
      !isAdBlocked &&
      !isAdFreePath &&
      (!user || (user.user_type !== "admin" && user.user_type !== "contractor" && user.user_type !== "sales"));

  const handleShowTutorial = () => {
    setShowTutorial(true);
  };

  const handleCloseTutorial = () => {
    setShowTutorial(false);
    localStorage.setItem("hasSeenTutorial", "true");
  };

  // 3. Create NEW handler for the finish button
  const handleFinishTutorial = () => {
    handleCloseTutorial();
    navigate('/chat');
  };

  // Don't show the button on admin pages, the chat page, or if the modal is already open
  const showFloatingButton =
    !location.pathname.startsWith('/admin') &&
    !location.pathname.startsWith('/chat') &&
    !isWidgetPath &&
    !location.pathname.startsWith('/quote-check') &&
    !showTutorial &&
    isHomeownerOrGuest;

  return (
    <AdProvider shouldShowAds={shouldShowAds}>
    <div className={`app-wrapper${isChatPath ? " app-wrapper--chat" : ""}`}>
      {skipLink}
      {/* Baseline robots tag — page-level <SEO> components override this for
          private/admin routes. Keeps prerendered output and SPA-mount races
          on a sane "index, follow" default. */}
      <Helmet>
        <meta name="robots" content="index, follow" />
      </Helmet>
      <WebSiteSchema />
      <LocalBusinessSchema />
      {/* Meta Pixel is an advertising tag — gated on marketing consent;
          Clarity is analytics — gated on analytics consent (via shouldLoadClarity). */}
      {consent.marketing && !isWidgetPath && <MetaPixel consentGiven={consent.marketing} />}
      {shouldLoadClarity && <MicrosoftClarity consentGiven={consentGiven} marketingConsent={consent.marketing} />}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        progressStyle={{ background: '#E8940A' }}
      />

      <SessionExpiredModal />
      <div
        className={`app-content${isChatPath ? " app-content--chat" : ""}`}
        style={{ display: isChatPath ? "flex" : "block" }}
      >
        {!hideChrome && <Toolbar />}
        <main id="main-content" ref={mainContentRef} tabIndex={-1} className={`app-main${isChatPath ? " app-main--chat" : ""}`}>
          {/* Never inside the chat shell: .app-main--chat is a flex row on
              mobile, so an in-flow banner would sit BESIDE the chat and
              squeeze it. Chat is also the one screen that must stay an
              exact viewport fit. */}
          {isAdBlocked && !isChatPath && (
            <div className="notice-banner">
              <div className="support-banner">
                <h2>Support Our Free Content!</h2>
                <p>
                  We rely on ads to keep Pricing Penguin free for everyone.
                  Please disable your ad blocker to enjoy the full experience
                  and support our mission to help homeowners like you.
                </p>
                <Link
                  to="/disable-adblock"
                  className="support-button"
                  onClick={() =>
                    trackEvent("AdBlock", "Click", "Disable AdBlock Link")
                  }
                >
                  Learn How to Disable
                </Link>
              </div>
            </div>
          )}
          
          <div className="content-wrapper">
            <div className="app-container app-fade-in">
              <AppRoutes
                jobCategories={jobCategories}
                subscriptionPackages={subscriptionPackages}
                reviews={reviews}
                isDataLoading={isDataLoading}
                isReviewsLoading={isReviewsLoading}
                reviewsError={reviewsError}
                retryReviews={retryReviews}
                // No longer passing onShowTutorial here
              />
            </div>
          </div>
        </main>
        {!hideChrome && !location.pathname.startsWith('/messages') && !location.pathname.startsWith('/chat') && <Footer />}
      </div>
      {!isPrerendering && !isWidgetPath && (
        <CookieBanner
          consent={consent}
          consentDecided={consentDecided}
          settingsOpen={settingsOpen}
          acceptAll={acceptAll}
          rejectAll={rejectAll}
          saveChoices={saveChoices}
          closeSettings={closeSettings}
        />
      )}

      {/* 4. Pass the correct handlers */}
      {showTutorial && (
        <WelcomeTutorial 
          onClose={handleCloseTutorial} 
          onFinish={handleFinishTutorial} 
        />
      )}

      {/* NEW FLOATING BUTTON */}
      {showFloatingButton && (
        <button className="floating-tutorial-btn" onClick={handleShowTutorial} title="See How It Works">
          <i className="fas fa-play-circle"></i>
          <span>How It Works</span>
        </button>
      )}

    </div>
    </AdProvider>
  );
}

export default App;
